/** Focus page: a real session timer bound to the next scheduled focus task, with sessions saved. */
import { $, $$, byId, esc, on, toast } from "../lib/dom";
import { fmtMMSS, fmtTime, sameDay } from "../lib/time";
import type { CalendarEvent, FocusSession, Task } from "../data/types";
import { saveFocusSession, updateTask } from "../data/store";

const MIXES = [
  { id: "jfKfPfyJRdk", title: "Lofi focus radio" },
  { id: "4xDzrJKXOOY", title: "Synthwave focus radio" },
  { id: "5qap5aO4i9A", title: "Beats to relax and focus" },
  { id: "eo1gKGt6h9M", title: "Hyper focus · ambient flow" },
];
let mixIndex = -1;
let seconds = 0, running = false, timer: number | undefined, startedAt: Date | null = null;
let current: Task | null = null;
let skipped = new Set<string>();

function candidates(tasks: Task[]) {
  const now = new Date();
  return tasks
    .filter((t) => t.status !== "done" && t.status !== "suggested" && !skipped.has(t.id))
    .sort((a, b) => {
      const as = a.scheduledStart && sameDay(a.scheduledStart, now) ? a.scheduledStart.getTime() : Infinity;
      const bs = b.scheduledStart && sameDay(b.scheduledStart, now) ? b.scheduledStart.getTime() : Infinity;
      return as - bs || b.priority - a.priority;
    });
}

function paintTime() {
  const goal = (current?.durationMin ?? 90) * 60;
  byId("stopwatchTime")!.firstChild!.nodeValue = fmtMMSS(seconds);
  const deg = Math.min(360, (seconds / goal) * 360);
  byId("stopwatchRing")!.style.background = `conic-gradient(var(--green) ${deg}deg, #e3e6df ${deg}deg)`;
}

export function renderFocus(tasks: Task[], events: CalendarEvent[], sessions: FocusSession[]) {
  const page = byId("page-focus");
  if (!page) return;
  const today = new Date();
  const blocks = events.filter((e) => e.kind === "focus" && sameDay(e.start, today)).sort((a, b) => a.start.getTime() - b.start.getTime());
  const protectedMin = blocks.reduce((a, e) => a + (e.end.getTime() - e.start.getTime()) / 60_000, 0);
  const focusedToday = sessions.filter((s) => sameDay(s.startedAt, today)).reduce((a, s) => a + s.seconds, 0);
  $(".eyebrow", page)!.textContent = `Protected time · ${Math.floor(protectedMin / 60)}h ${protectedMin % 60}m today · ${Math.round(focusedToday / 60)} min focused`;
  $$(".focus-card b").forEach((b) => (b.textContent = protectedMin ? `${Math.floor(protectedMin / 60)}h ${protectedMin % 60}m protected` : "No focus time yet"));
  $$(".focus-card small").forEach((s) => (s.textContent = protectedMin ? "Timo found room for your most important work." : "Add a task and Timo will find room for it."));

  if (!running) current = candidates(tasks)[0] ?? null;
  $(".focus-task-name", page)!.textContent = current ? current.title : "No task queued, add one and Timo will find time";
  $("#stopwatchTime small")!.textContent = `${current?.durationMin ?? 90} minute goal`;
  paintTime();

  const plan = $(".focus-plan-card", page)!;
  plan.innerHTML = `<div class="section-head"><h2>Today's focus blocks</h2><span class="progress">${Math.floor(protectedMin / 60)}h ${protectedMin % 60}m</span></div>` +
    (blocks.map((e) => { const now = today >= e.start && today < e.end; return `<div class="focus-plan-row"><span class="focus-plan-time">${now ? "Now" : fmtTime(e.start)}</span><span><b>${esc(e.title)}</b><span>${Math.round((e.end.getTime() - e.start.getTime()) / 60_000)} min · ${e.source === "scheduler" ? "Scheduled by Timo" : "Flexible"}</span></span><span class="tag">${now ? "Current" : today > e.end ? "Done" : "Later"}</span></div>`; }).join("") || `<p class="lead" style="font-size:13px;padding:6px 0">No focus blocks today. Schedule a task from the inbox or task list.</p>`);
}

function rotateMix() {
  mixIndex = (mixIndex + 1 + Math.floor(Math.random() * (MIXES.length - 1))) % MIXES.length;
  const mix = MIXES[mixIndex]!;
  byId<HTMLIFrameElement>("focusPlayer")!.src = `https://www.youtube-nocookie.com/embed/${mix.id}?rel=0&modestbranding=1`;
  byId("focusMixTitle")!.textContent = mix.title;
  byId("mixPosition")!.textContent = `${mixIndex + 1} of ${MIXES.length}`;
}

export function setupFocus(getTasks: () => Task[]) {
  on(byId("startFocus"), "click", (e) => {
    running = !running;
    if (running && !startedAt) startedAt = new Date();
    (e.currentTarget as HTMLElement).querySelector("span")!.textContent = running ? "Pause focus" : "Resume focus";
    byId("focusState")!.lastChild!.nodeValue = running ? " Focusing now" : " Session paused";
    window.clearInterval(timer);
    if (running) {
      timer = window.setInterval(() => { seconds++; paintTime(); }, 1000);
      if (current && current.status === "open") void updateTask(current.id, { notes: current.notes }, "user");
    }
  });
  on(byId("resetFocus"), "click", () => { window.clearInterval(timer); running = false; seconds = 0; startedAt = null; paintTime(); byId("startFocus")!.querySelector("span")!.textContent = "Start focus"; byId("focusState")!.lastChild!.nodeValue = " Ready to focus"; });
  on(byId("finishFocus"), "click", async () => {
    window.clearInterval(timer);
    if (!seconds) return toast("Start a session first");
    const done = seconds;
    await saveFocusSession({ taskId: current?.id ?? null, title: current?.title ?? "Focus", seconds: done, startedAt: startedAt ?? new Date() });
    if (current && confirm(`Mark ${current.title} as done?`)) await updateTask(current.id, { status: "done", completedAt: new Date() }, "user");
    running = false; seconds = 0; startedAt = null; paintTime();
    byId("startFocus")!.querySelector("span")!.textContent = "Start focus";
    byId("focusState")!.lastChild!.nodeValue = " Session complete";
    toast(done < 60 ? `${done} focused seconds saved` : `${Math.round(done / 60)} focused minutes saved`);
  });
  on(byId("skipFocusTask"), "click", () => { if (current) skipped.add(current.id); if (skipped.size >= candidates(getTasks()).length + skipped.size) skipped = new Set(); const next = candidates(getTasks())[0]; toast(next ? `Next: ${next.title}` : "No other tasks queued"); renderFocus(getTasks(), [], []); document.dispatchEvent(new CustomEvent("timo:rerender")); });
  on(byId("newFocusMix"), "click", () => { rotateMix(); toast("A fresh focus mix is ready"); });
  rotateMix();
}
